import { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import { WeeklyRosterBreakdown } from '@/components/WeeklyRosterBreakdown';
import type { Player } from '@/types/player';

// Test roster data for consistent testing
const testRoster: Player[] = [
  {
    id: '1',
    name: 'Jalen Hurts',
    position: 'QB',
    team: 'PHI',
    imageUrl: 'https://a.espncdn.com/i/headshots/nfl/players/full/3917315.png',
    fantasyRank: 1,
    adp: 12.5,
    projectedPoints: 24.8,
    ownership: 85.2,
    value: 92.5
  },
  {
    id: '2', 
    name: 'Christian McCaffrey',
    position: 'RB',
    team: 'SF',
    imageUrl: 'https://a.espncdn.com/i/headshots/nfl/players/full/3116385.png',
    fantasyRank: 2,
    adp: 8.1,
    projectedPoints: 22.3,
    ownership: 92.1,
    value: 98.7
  },
  {
    id: '3',
    name: 'Tyreek Hill',
    position: 'WR',
    team: 'MIA',
    imageUrl: 'https://a.espncdn.com/i/headshots/nfl/players/full/2976499.png',
    fantasyRank: 3,
    adp: 15.2,
    projectedPoints: 18.9,
    ownership: 78.4,
    value: 88.2
  },
  {
    id: '4',
    name: 'Deebo Samuel',
    position: 'WR',
    team: 'SF',
    imageUrl: 'https://a.espncdn.com/i/headshots/nfl/players/full/4035687.png',
    fantasyRank: 12,
    adp: 32.1,
    projectedPoints: 16.2,
    ownership: 65.8,
    value: 82.1
  },
  {
    id: '5',
    name: 'Travis Kelce',
    position: 'TE',
    team: 'KC',
    imageUrl: 'https://a.espncdn.com/i/headshots/nfl/players/full/15847.png',
    fantasyRank: 8,
    adp: 28.9,
    projectedPoints: 14.7,
    ownership: 81.3,
    value: 85.9
  },
  {
    id: '6',
    name: 'Saquon Barkley',
    position: 'RB',
    team: 'PHI',
    imageUrl: 'https://a.espncdn.com/i/headshots/nfl/players/full/3929630.png',
    fantasyRank: 6,
    adp: 22.4,
    projectedPoints: 20.1,
    ownership: 74.2,
    value: 89.4
  },
  {
    id: '7',
    name: 'Trevor Etienne',
    position: 'RB',
    team: 'JAX',
    imageUrl: 'https://a.espncdn.com/i/headshots/nfl/players/full/5083322.png',
    fantasyRank: 48,
    adp: 85.6,
    projectedPoints: 8.2,
    ownership: 32.1,
    value: 45.3
  },
  {
    id: '8',
    name: 'Devin Neal',
    position: 'RB',
    team: 'KC',
    imageUrl: 'https://a.espncdn.com/i/headshots/nfl/players/full/5083451.png',
    fantasyRank: 52,
    adp: 92.1,
    projectedPoints: 7.8,
    ownership: 28.4,
    value: 41.7
  },
  {
    id: '9',
    name: 'Harrison Butker',
    position: 'K',
    team: 'KC',
    imageUrl: 'https://a.espncdn.com/i/headshots/nfl/players/full/3051392.png',
    fantasyRank: 2,
    adp: 165.8,
    projectedPoints: 8.9,
    ownership: 45.2,
    value: 72.1
  },
  {
    id: '10',
    name: 'San Francisco',
    position: 'DEF',
    team: 'SF',
    imageUrl: 'https://a.espncdn.com/i/teamlogos/nfl/500/sf.png',
    fantasyRank: 5,
    adp: 158.3,
    projectedPoints: 7.2,
    ownership: 38.7,
    value: 68.4
  }
];

const testLeagueSettings = {
  teamCount: 12,
  roster: {
    QB: 1,
    RB: 2,
    WR: 2,
    TE: 1,
    K: 1,
    DEF: 1,
    FLEX: 1,
    SUPERFLEX: 0
  },
  scoring: 'PPR' as const
};

export default function TestWeeklyBreakdown() {
  const [refreshKey, setRefreshKey] = useState(0);
  
  const handleRefresh = () => {
    // Clear any cached data and force re-render
    const currentDate = new Date().toISOString().split('T')[0];
    const cacheKeys = Object.keys(localStorage).filter(key => 
      key.startsWith('weekly-breakdown-') && key.includes(currentDate)
    );
    cacheKeys.forEach(key => localStorage.removeItem(key));
    
    // Force component re-render
    setRefreshKey(prev => prev + 1);
  };

  return (
    <div className="min-h-screen bg-black py-8 sm:py-12">
      <Helmet>
        <title>Test Weekly Breakdown | Fantasy Fiesta</title>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>
      
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="max-w-7xl mx-auto px-3 sm:px-4 md:px-6 lg:px-8"
      >
        {/* Hidden Refresh Button - Only visible on hover */}
        <div className="fixed top-4 right-4 z-50 opacity-20 hover:opacity-100 transition-opacity">
          <motion.button
            onClick={handleRefresh}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            className="px-3 py-2 bg-fiesta-blue hover:bg-fiesta-blue/80 text-white rounded-lg text-sm font-medium transition-colors shadow-lg flex items-center gap-1"
            title="Generate New Breakdown (Test Only)"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
            Refresh
          </motion.button>
        </div>

        {/* Weekly Breakdown Component - Exact same as My Roster page */}
        <div key={refreshKey}>
          <WeeklyRosterBreakdown
            roster={testRoster}
            leagueSettings={testLeagueSettings}
          />
        </div>
      </motion.div>
    </div>
  );
}